import { useState, useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button } from '../../../button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import {
  useCreateCarSaleMutation,
  useUpdateCarSaleMutation,
  IFindCarSaleQuery,
} from '../../../../services/carSaleServices/__generated__/queries.generated';
import { useGetMyDealerQuery } from '../../../../services/dealerService/__generated__/queries.generated';
import { toast } from 'react-toastify';
import { IBuyerInfoInput } from '../../../../../__generated__/graphql';
import { useSearchParams } from 'react-router-dom';
import { FIND_CAR_SALE } from '../../../../services/carSaleServices/queries';
import { Modal } from '../../../modal';

const BuyerSellerSection = styled.div`
  ${tw`flex flex-col items-center grid grid-cols-3 mb-6 w-full`}
`;

const InfoBox = styled.div`
  ${tw`border border-gray-300 p-2 border-solid relative min-h-[230px]`}
`;

const Label = styled.span`
  ${tw`text-sm font-semibold mr-2`}
`;

const Text = styled.div`
  ${tw`text-sm mb-2`}
`;

const ContactRow = styled.div`
  ${tw`flex mb-2`}
`;

const Input = styled.input`
  ${tw`border border-gray-300 p-2 border-solid h-[44px]`}
`;

const AddressGroup = styled.div`
  ${tw`grid grid-cols-3 gap-2 mb-2`}
`;

const EditingContainer = styled.div`
  ${tw`flex flex-col items-center w-full justify-center`}
`;

const dealerInfo = {
  name: 'Autopilotee Cars',
  streetAddress: '2150 N. 1st St, San Jose, CA 95131',
  city: 'San Jose',
  state: 'CA',
  zipcode: '95131',
  phone: '+1(866)217-2849',
  email: 'contact@autopilotee.com',
};

const BuyerSellerSectionComponent = ({
  agreementId,
  setAgreementIdState,
  agreementData,
}: {
  agreementId: string;
  setAgreementIdState: (agreementId: string) => void;
  agreementData: IFindCarSaleQuery | undefined;
}) => {
  const [buyerInfo, setBuyerInfo] = useState({
    name: '',
    streetAddress: '',
    city: '',
    state: '',
    zipcode: '',
    phone: '',
    email: '',
    county: '',
  });
  const [isEditing, setIsEditing] = useState(false);

  const [coBuyerInfo, setCoBuyerInfo] = useState({
    name: '',
    streetAddress: '',
    city: '',
    state: '',
    zipcode: '',
    phone: '',
    email: '',
    county: '',
  });

  const [sellerInfo, setSellerInfo] = useState(dealerInfo);

  const { data: dealerData } = useGetMyDealerQuery();
  const [CREATE_CAR_SALE] = useCreateCarSaleMutation({
    onCompleted: data => {
      setAgreementIdState(data.createCarSale.id);
      setIsEditing(false);
      toast.success('Buyer and seller info updated successfully');
    },
    onError: error => {
      toast.error('Error creating car sale');
    },
  });

  const [UPDATE_CAR_SALE] = useUpdateCarSaleMutation({
    refetchQueries: [FIND_CAR_SALE],
    onCompleted: data => {
      setIsEditing(false);
      toast.success('Buyer and seller info updated successfully');
    },
    onError: error => {
      toast.error('Error updating car sale');
    },
  });

  useEffect(() => {
    if (agreementData?.carSale?.id) {
      setBuyerInfo(agreementData.carSale.buyer[0]);
      if (agreementData.carSale.buyer.length > 1) {
        setCoBuyerInfo(agreementData.carSale.buyer[1]);
      }
    }
  }, [agreementData]);

  const handleSaveOrUpdateBuyerAndSellerInfo = async () => {
    if (!dealerData?.myDealer?.id) {
      toast.error('Dealer ID not found');
      return;
    }
    const buyer = [
      {
        name: buyerInfo.name,
        streetAddress: buyerInfo.streetAddress,
        city: buyerInfo.city,
        state: buyerInfo.state,
        zipcode: buyerInfo.zipcode,
        phone: buyerInfo.phone,
        email: buyerInfo.email,
        county: buyerInfo.county,
      },
      {
        name: coBuyerInfo.name,
        streetAddress: coBuyerInfo.streetAddress,
        city: coBuyerInfo.city,
        state: coBuyerInfo.state,
        zipcode: coBuyerInfo.zipcode,
        phone: coBuyerInfo.phone,
        email: coBuyerInfo.email,
        county: coBuyerInfo.county,
      },
    ] as IBuyerInfoInput[];
    if (agreementData?.carSale?.id) {
      await UPDATE_CAR_SALE({
        variables: {
          input: {
            id: agreementData.carSale.id,
            buyer,
          },
        },
      });
    } else {
      await CREATE_CAR_SALE({
        variables: {
          input: {
            buyer,
            dealerId: dealerData.myDealer.id,
          },
        },
      });
    }
  };

  const handleBuyerEdit = () => {
    setIsEditing(true);
  };

  const handleBuyerSave = () => {
    setIsEditing(false);
  };

  const handleCoBuyerEdit = () => {
    setIsEditing(true);
  };

  const handleCoBuyerSave = () => {
    setIsEditing(false);
  };

  const handleSellerEdit = () => {
    setIsEditing(true);
  };

  const handleSellerSave = () => {
    setIsEditing(false);
  };

  const renderBuyerInfoSection = () => {
    return (
      <>
        <InfoBox>
          <Label>Buyer Name:</Label>
          {isEditing ? (
            <Input
              value={buyerInfo.name}
              onChange={e => setBuyerInfo({ ...buyerInfo, name: e.target.value })}
            />
          ) : (
            <Text>{buyerInfo.name || 'N/A'}</Text>
          )}
          <Label>Address:</Label>
          {isEditing ? (
            <>
              <Input
                value={buyerInfo.streetAddress}
                onChange={e =>
                  setBuyerInfo({
                    ...buyerInfo,
                    streetAddress: e.target.value,
                  })
                }
              />
              <AddressGroup>
                <Input
                  placeholder="City"
                  value={buyerInfo.city}
                  onChange={e => setBuyerInfo({ ...buyerInfo, city: e.target.value })}
                />
                <Input
                  placeholder="State"
                  value={buyerInfo.state}
                  onChange={e => setBuyerInfo({ ...buyerInfo, state: e.target.value })}
                />
                <Input
                  placeholder="ZIP Code"
                  value={buyerInfo.zipcode}
                  onChange={e => setBuyerInfo({ ...buyerInfo, zipcode: e.target.value })}
                />
              </AddressGroup>
            </>
          ) : (
            <>
              <Text>{buyerInfo.streetAddress || 'N/A'}</Text>
              <Text>
                {buyerInfo.city || buyerInfo.state || buyerInfo.zipcode
                  ? `${buyerInfo.city || 'N/A'}, ${buyerInfo.state || 'N/A'} ${
                      buyerInfo.zipcode || 'N/A'
                    }`
                  : 'N/A'}
              </Text>
            </>
          )}
          <ContactRow>
            <Label>Cell:</Label>
            {isEditing ? (
              <Input
                value={buyerInfo.phone}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setBuyerInfo({ ...buyerInfo, phone: e.target.value })
                }
              />
            ) : (
              <Text>{buyerInfo.phone || 'N/A'}</Text>
            )}
          </ContactRow>
          <ContactRow>
            <Label>Email:</Label>
            {isEditing ? (
              <Input
                value={buyerInfo.email}
                onChange={e => setBuyerInfo({ ...buyerInfo, email: e.target.value })}
              />
            ) : (
              <Text>{buyerInfo.email || 'N/A'}</Text>
            )}
          </ContactRow>
          <ContactRow>
            <Label>County:</Label>
            {isEditing ? (
              <Input
                value={buyerInfo.county}
                onChange={e => setBuyerInfo({ ...buyerInfo, county: e.target.value })}
              />
            ) : (
              <Text>{buyerInfo.county || 'N/A'}</Text>
            )}
          </ContactRow>
        </InfoBox>

        <InfoBox>
          <Label>Co-Buyer Name:</Label>
          {isEditing ? (
            <Input
              value={coBuyerInfo.name}
              onChange={e => setCoBuyerInfo({ ...coBuyerInfo, name: e.target.value })}
            />
          ) : (
            <Text>{coBuyerInfo.name || 'N/A'}</Text>
          )}
          <Label>Address:</Label>
          {isEditing ? (
            <>
              <Input
                value={coBuyerInfo.streetAddress}
                onChange={e =>
                  setCoBuyerInfo({
                    ...coBuyerInfo,
                    streetAddress: e.target.value,
                  })
                }
              />
              <AddressGroup>
                <Input
                  placeholder="City"
                  value={coBuyerInfo.city}
                  onChange={e => setCoBuyerInfo({ ...coBuyerInfo, city: e.target.value })}
                />
                <Input
                  placeholder="State"
                  value={coBuyerInfo.state}
                  onChange={e => setCoBuyerInfo({ ...coBuyerInfo, state: e.target.value })}
                />
                <Input
                  placeholder="ZIP Code"
                  value={coBuyerInfo.zipcode}
                  onChange={e =>
                    setCoBuyerInfo({
                      ...coBuyerInfo,
                      zipcode: e.target.value,
                    })
                  }
                />
              </AddressGroup>
            </>
          ) : (
            <>
              <Text>{coBuyerInfo.streetAddress || 'N/A'}</Text>
              <Text>
                {coBuyerInfo.city || coBuyerInfo.state || coBuyerInfo.zipcode
                  ? `${coBuyerInfo.city || 'N/A'}, ${coBuyerInfo.state || 'N/A'} ${
                      coBuyerInfo.zipcode || 'N/A'
                    }`
                  : 'N/A'}
              </Text>
            </>
          )}
          <ContactRow>
            <Label>Cell:</Label>
            {isEditing ? (
              <Input
                value={coBuyerInfo.phone}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCoBuyerInfo({ ...coBuyerInfo, phone: e.target.value })
                }
              />
            ) : (
              <Text>{coBuyerInfo.phone || 'N/A'}</Text>
            )}
          </ContactRow>
          <ContactRow>
            <Label>Email:</Label>
            {isEditing ? (
              <Input
                value={coBuyerInfo.email}
                onChange={e => setCoBuyerInfo({ ...coBuyerInfo, email: e.target.value })}
              />
            ) : (
              <Text>{coBuyerInfo.email || 'N/A'}</Text>
            )}
          </ContactRow>
          <ContactRow>
            <Label>County:</Label>
            {isEditing ? (
              <Input
                value={coBuyerInfo.county}
                onChange={e => setCoBuyerInfo({ ...coBuyerInfo, county: e.target.value })}
              />
            ) : (
              <Text>{coBuyerInfo.county || 'N/A'}</Text>
            )}
          </ContactRow>
        </InfoBox>

        <InfoBox>
          <Label>Seller-Creditor (Name and Address)</Label>
          {isEditing ? (
            <>
              <Input
                value={sellerInfo.name}
                onChange={e => setSellerInfo({ ...sellerInfo, name: e.target.value })}
              />
              <Input
                value={sellerInfo.streetAddress}
                onChange={e =>
                  setSellerInfo({
                    ...sellerInfo,
                    streetAddress: e.target.value,
                  })
                }
              />
              <AddressGroup>
                <Input
                  placeholder="City"
                  value={sellerInfo.city}
                  onChange={e => setSellerInfo({ ...sellerInfo, city: e.target.value })}
                />
                <Input
                  placeholder="State"
                  value={sellerInfo.state}
                  onChange={e => setSellerInfo({ ...sellerInfo, state: e.target.value })}
                />
                <Input
                  placeholder="ZIP Code"
                  value={sellerInfo.zipcode}
                  onChange={e => setSellerInfo({ ...sellerInfo, zipcode: e.target.value })}
                />
              </AddressGroup>
            </>
          ) : (
            <>
              <Text>{sellerInfo.name || 'N/A'}</Text>
              <Text>{sellerInfo.streetAddress || 'N/A'}</Text>
              <Text>
                {sellerInfo.city || sellerInfo.state || sellerInfo.zipcode
                  ? `${sellerInfo.city || 'N/A'}, ${sellerInfo.state || 'N/A'} ${
                      sellerInfo.zipcode || 'N/A'
                    }`
                  : 'N/A'}
              </Text>
            </>
          )}
          <ContactRow>
            <Label>Cell:</Label>
            {isEditing ? (
              <Input
                value={sellerInfo.phone}
                onChange={e => setSellerInfo({ ...sellerInfo, phone: e.target.value })}
              />
            ) : (
              <Text>{sellerInfo.phone || 'N/A'}</Text>
            )}
          </ContactRow>
          <ContactRow>
            <Label>Email:</Label>
            {isEditing ? (
              <Input
                value={sellerInfo.email}
                onChange={e => setSellerInfo({ ...sellerInfo, email: e.target.value })}
              />
            ) : (
              <Text>{sellerInfo.email || 'N/A'}</Text>
            )}
          </ContactRow>
        </InfoBox>
      </>
    );
  };
  if (isEditing) {
    return (
      <Modal isOpen={true} onClose={() => setIsEditing(false)}>
        <EditingContainer>
          <BuyerSellerSection>{renderBuyerInfoSection()}</BuyerSellerSection>
          <Button
            text="Save"
            onClick={handleSaveOrUpdateBuyerAndSellerInfo}
            buttonWidth="50px"
            buttonHeight="40px"
          />
        </EditingContainer>
      </Modal>
    );
  }
  return (
    <BuyerSellerSection onClick={handleBuyerEdit}>{renderBuyerInfoSection()}</BuyerSellerSection>
  );
};

export default BuyerSellerSectionComponent;
